
export interface Badge {
  id: string
  name: string
  description: string
  icon: string
  color: string
  bgColor: string
  borderColor: string
  requirement: string
  category: 'posting' | 'engagement' | 'social' | 'reputation' | 'special'
  tier: 'bronze' | 'silver' | 'gold' | 'platinum' | 'legendary'
}

export interface ReputationLevel {
  level: number
  name: string
  minPoints: number
  maxPoints: number
  color: string
  bgColor: string
  icon: string
}

export const reputationLevels: ReputationLevel[] = [
  { level: 1, name: 'Newcomer', minPoints: 0, maxPoints: 49, color: 'text-gray-500', bgColor: 'bg-gray-100', icon: 'ri-seedling-line' },
  { level: 2, name: 'Regular', minPoints: 50, maxPoints: 149, color: 'text-emerald-600', bgColor: 'bg-emerald-50', icon: 'ri-plant-line' },
  { level: 3, name: 'Contributor', minPoints: 150, maxPoints: 349, color: 'text-sky-600', bgColor: 'bg-sky-50', icon: 'ri-quill-pen-line' },
  { level: 4, name: 'Enthusiast', minPoints: 350, maxPoints: 699, color: 'text-amber-600', bgColor: 'bg-amber-50', icon: 'ri-fire-line' },
  { level: 5, name: 'Expert', minPoints: 700, maxPoints: 1499, color: 'text-orange-600', bgColor: 'bg-orange-50', icon: 'ri-award-line' },
  { level: 6, name: 'Master', minPoints: 1500, maxPoints: 2999, color: 'text-rose-600', bgColor: 'bg-rose-50', icon: 'ri-vip-crown-line' },
  { level: 7, name: 'Legend', minPoints: 3000, maxPoints: Infinity, color: 'text-amber-500', bgColor: 'bg-gradient-to-r from-amber-50 to-orange-50', icon: 'ri-vip-diamond-line' },
]

export const allBadges: Badge[] = [
  // Posting badges
  {
    id: 'first-post',
    name: 'First Words',
    description: 'Created your first forum post',
    icon: 'ri-edit-line',
    color: 'text-amber-700',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    requirement: 'Create 1 post',
    category: 'posting',
    tier: 'bronze',
  },
  {
    id: 'prolific-writer',
    name: 'Prolific Writer',
    description: 'Created 10 forum posts',
    icon: 'ri-file-text-line',
    color: 'text-gray-500',
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-200',
    requirement: 'Create 10 posts',
    category: 'posting',
    tier: 'silver',
  },
  {
    id: 'storyteller',
    name: 'Storyteller',
    description: 'Created 50 forum posts',
    icon: 'ri-book-open-line',
    color: 'text-amber-500',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-300',
    requirement: 'Create 50 posts',
    category: 'posting',
    tier: 'gold',
  },
  {
    id: 'drama-chronicler',
    name: 'Drama Chronicler',
    description: 'Created 100 forum posts',
    icon: 'ri-quill-pen-line',
    color: 'text-cyan-600',
    bgColor: 'bg-cyan-50',
    borderColor: 'border-cyan-300',
    requirement: 'Create 100 posts',
    category: 'posting',
    tier: 'platinum',
  },

  // Engagement badges
  {
    id: 'first-comment',
    name: 'Conversation Starter',
    description: 'Left your first comment',
    icon: 'ri-chat-1-line',
    color: 'text-amber-700',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    requirement: 'Leave 1 comment',
    category: 'engagement',
    tier: 'bronze',
  },
  {
    id: 'active-commenter',
    name: 'Active Voice',
    description: 'Left 25 comments',
    icon: 'ri-chat-3-line',
    color: 'text-gray-500',
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-200',
    requirement: 'Leave 25 comments',
    category: 'engagement',
    tier: 'silver',
  },
  {
    id: 'discussion-leader',
    name: 'Discussion Leader',
    description: 'Left 100 comments',
    icon: 'ri-discuss-line',
    color: 'text-amber-500',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-300',
    requirement: 'Leave 100 comments',
    category: 'engagement',
    tier: 'gold',
  },
  {
    id: 'community-pillar',
    name: 'Community Pillar',
    description: 'Left 500 comments',
    icon: 'ri-building-line',
    color: 'text-cyan-600',
    bgColor: 'bg-cyan-50',
    borderColor: 'border-cyan-300',
    requirement: 'Leave 500 comments',
    category: 'engagement',
    tier: 'platinum',
  },

  // Social badges
  {
    id: 'friendly-face',
    name: 'Friendly Face',
    description: 'Gained your first follower',
    icon: 'ri-user-heart-line',
    color: 'text-amber-700',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    requirement: 'Get 1 follower',
    category: 'social',
    tier: 'bronze',
  },
  {
    id: 'popular-member',
    name: 'Popular Member',
    description: 'Gained 25 followers',
    icon: 'ri-group-line',
    color: 'text-gray-500',
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-200',
    requirement: 'Get 25 followers',
    category: 'social',
    tier: 'silver',
  },
  {
    id: 'community-star',
    name: 'Community Star',
    description: 'Gained 100 followers',
    icon: 'ri-star-smile-line',
    color: 'text-amber-500',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-300',
    requirement: 'Get 100 followers',
    category: 'social',
    tier: 'gold',
  },
  {
    id: 'influencer',
    name: 'Influencer',
    description: 'Gained 500 followers',
    icon: 'ri-vip-crown-line',
    color: 'text-cyan-600',
    bgColor: 'bg-cyan-50',
    borderColor: 'border-cyan-300',
    requirement: 'Get 500 followers',
    category: 'social',
    tier: 'platinum',
  },

  // Reputation badges
  {
    id: 'rising-star',
    name: 'Rising Star',
    description: 'Reached 50 reputation points',
    icon: 'ri-rocket-line',
    color: 'text-amber-700',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    requirement: '50 reputation',
    category: 'reputation',
    tier: 'bronze',
  },
  {
    id: 'trusted-member',
    name: 'Trusted Member',
    description: 'Reached 200 reputation points',
    icon: 'ri-shield-check-line',
    color: 'text-gray-500',
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-200',
    requirement: '200 reputation',
    category: 'reputation',
    tier: 'silver',
  },
  {
    id: 'drama-authority',
    name: 'Drama Authority',
    description: 'Reached 500 reputation points',
    icon: 'ri-medal-line',
    color: 'text-amber-500',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-300',
    requirement: '500 reputation',
    category: 'reputation',
    tier: 'gold',
  },
  {
    id: 'legendary-status',
    name: 'Legendary Status',
    description: 'Reached 1000 reputation points',
    icon: 'ri-vip-diamond-line',
    color: 'text-cyan-600',
    bgColor: 'bg-cyan-50',
    borderColor: 'border-cyan-300',
    requirement: '1000 reputation',
    category: 'reputation',
    tier: 'platinum',
  },

  // Special badges
  {
    id: 'early-adopter',
    name: 'Early Adopter',
    description: 'Joined during the first month of launch',
    icon: 'ri-flashlight-line',
    color: 'text-rose-500',
    bgColor: 'bg-rose-50',
    borderColor: 'border-rose-300',
    requirement: 'Join early',
    category: 'special',
    tier: 'legendary',
  },
  {
    id: 'drama-reviewer',
    name: 'Drama Critic',
    description: 'Posted 5 drama reviews with ratings',
    icon: 'ri-movie-line',
    color: 'text-amber-500',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-300',
    requirement: 'Post 5 reviews',
    category: 'special',
    tier: 'gold',
  },
  {
    id: 'group-creator',
    name: 'Community Builder',
    description: 'Created a group',
    icon: 'ri-team-line',
    color: 'text-gray-500',
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-200',
    requirement: 'Create a group',
    category: 'special',
    tier: 'silver',
  },
  {
    id: 'helpful-voter',
    name: 'Helpful Voter',
    description: 'Upvoted 50 posts',
    icon: 'ri-thumb-up-line',
    color: 'text-amber-700',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    requirement: 'Upvote 50 posts',
    category: 'special',
    tier: 'bronze',
  },
]

export const tierConfig = {
  bronze: { label: 'Bronze', color: 'text-amber-700', bgColor: 'bg-amber-100', borderColor: 'border-amber-300', gradient: 'from-amber-600 to-amber-800' },
  silver: { label: 'Silver', color: 'text-gray-500', bgColor: 'bg-gray-100', borderColor: 'border-gray-300', gradient: 'from-gray-400 to-gray-600' },
  gold: { label: 'Gold', color: 'text-amber-500', bgColor: 'bg-amber-100', borderColor: 'border-amber-400', gradient: 'from-amber-400 to-amber-600' },
  platinum: { label: 'Platinum', color: 'text-cyan-600', bgColor: 'bg-cyan-100', borderColor: 'border-cyan-400', gradient: 'from-cyan-400 to-cyan-600' },
  legendary: { label: 'Legendary', color: 'text-rose-500', bgColor: 'bg-rose-100', borderColor: 'border-rose-400', gradient: 'from-rose-400 to-rose-600' },
}

export const getReputationLevel = (points: number): ReputationLevel => {
  for (let i = reputationLevels.length - 1; i >= 0; i--) {
    if (points >= reputationLevels[i].minPoints) {
      return reputationLevels[i]
    }
  }
  return reputationLevels[0]
}

export const getNextLevel = (points: number): ReputationLevel | null => {
  const current = getReputationLevel(points)
  const nextIndex = reputationLevels.findIndex(l => l.level === current.level) + 1
  if (nextIndex < reputationLevels.length) {
    return reputationLevels[nextIndex]
  }
  return null
}

export const getLevelProgress = (points: number): number => {
  const current = getReputationLevel(points)
  const next = getNextLevel(points)
  if (!next) return 100
  const range = next.minPoints - current.minPoints
  const progress = points - current.minPoints
  return Math.min(100, Math.round((progress / range) * 100))
}
